import { Component, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RouteService } from '../../../service/route.service';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { MAX_INT_32 } from '../../shared/constants/values';
import { debounceTime, finalize, firstValueFrom, Subject, takeUntil, Subscription } from 'rxjs';

@Component({
  selector: 'app-audit-report-new',
  templateUrl: './audit-report-new.component.html',
  styleUrl: './audit-report-new.component.scss'
})
export class AuditReportNewComponent {
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;

  getDataURL = API_ENDPOINTS.AUDIT_REPORT;
  getUserEmailURL = API_ENDPOINTS.USERS_ROLE;

  routes: string[];

  constructor(
    private routesService: RouteService,
    private http: HttpClient
  ) { }

  gridConfig: GridConfig = {
    columns: [
      { label: 'Table Name', jsonKey: 'tableName', dataType: 'string', isSortable: true },
      { label: 'Record', jsonKey: 'parentKey', dataType: 'string', isSortable: false },
      { label: 'Action', jsonKey: 'actionName', dataType: 'string', isSortable: true },
      { label: 'Column Name', jsonKey: 'columnName', dataType: 'string', isSortable: true },
      { label: 'Old Value', jsonKey: 'oldValue', dataType: 'string', isSortable: true },
      { label: 'New Value', jsonKey: 'newValue', dataType: 'string', isSortable: true },
      { label: 'Created By / Modified By', jsonKey: 'modifiedByUserEmail', dataType: 'string', isSortable: true },
      { label: 'Created At / Modified At', jsonKey: 'modifiedAt', dataType: 'date', width: 'full', isSortable: true }
    ],
    filters: [
      {
        label: 'Scope', jsonKey: 'auditReportName', options: {
          data: [{ id: 'All', name: 'All', checked: true },
          { id: 'PMSMaster', name: 'PMS', checked: true },
          { id: 'Practice', name: 'Practices', checked: true },
          { id: 'PMSObject', name: 'PMS Objects', checked: true },
          { id: 'PMSObjectWisePHI', name: 'PHI Records', checked: true },
          { id: 'LoadSchedule', name: 'Load Schedules', checked: true },
          { id: 'PracticeLoadConfiguration', name: 'Practice Load Configurations', checked: true },
          { id: 'DentrixAdjustmentType', name: 'Dentrix Adjustment Type', checked: true },
          { id: 'PMSSchemaIntegrity', name: 'PMS Schema Integrity', checked: true },
          { id: 'User', name: 'User', checked: true }
          ]
        }, type: 'multi'
      },
      { label: 'Users', jsonKey: 'userEmail', options: [], type: 'multi' },
      { label: 'Log Dates', jsonKey: 'logDates', options: [], type: 'date' },
    ],
    deletable: false,
    editable: false,
    exportable: true,
    uniqueTableName: 'AuditReport',
    displayHideAuditColumnsToggle: false,
  };

  ngOnInit() {
    this.fetchUserEmailOptions();
  }

  ngAfterViewInit(): void {
    this.initializeDefaultAuditFilters(false);
  }

  async fetchUserEmailOptions(): Promise<void> {
    const currentUserEmail = localStorage.getItem('userEmail')?.trim();

    const response = await firstValueFrom(
      this.http.get<any>(this.getUserEmailURL, { params: { pageSize: MAX_INT_32 } })
    );

    let emailList = response.data.map(option => ({
      ...option,
      name: option.email === currentUserEmail ? `${option.email} (Me)` : option.email,
      id: option.email
    })).sort((a, b) => a.name.localeCompare(b.name));

    emailList = emailList.filter(option => option.id !== 'pms_admin@dentalcorp.ca');
    emailList.unshift({
      name: 'pms_admin@dentalcorp.ca (System User)',
      id: 'pms_admin@dentalcorp.ca',
      checked: true
    });
    emailList.unshift({
      name: 'All',
      id: 'All',
      checked: true
    });

    emailList = emailList.map(opt => ({ ...opt, checked: true }));
    response.data = emailList;    
    this.gridConfig.filters.find(
      (filter) => filter.jsonKey === 'userEmail'
    ).options = response;

    this.initializeDefaultAuditFilters(true);
    
    // this.filters["userEmail"] = emailList;
    // this.emailOptions = this.filters["userEmail"];
  }

  // fetchUserEmailOptions() {
  //   // Make an API call to fetch practice load options
  //   this.http.get<any>(this.getUserEmailURL, { params: { pageSize: MAX_INT_32 } }).subscribe(response => {
  //     response.data = response.data.map(option => ({
  //       ...option,
  //       name: option.email,
  //       id: option.email
  //     })).sort((a, b) => a.name.localeCompare(b.name));
  //     this.gridConfig.filters.find(filter => filter.jsonKey === 'emailFilters').options = response;
  //   });
  // }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  private initializeDefaultAuditFilters(shouldRefresh: boolean): void {
    if (!this.dynamicGrid) {
      return;
    }

    const scopeFilter = this.gridConfig.filters.find(
      (filter) => filter.jsonKey === 'auditReportName'
    );
    const usersFilter = this.gridConfig.filters.find(
      (filter) => filter.jsonKey === 'userEmail'
    );

    const defaultScopeOptions =
      scopeFilter?.options?.data?.filter((option: any) => option.checked) || [];
    const defaultUserOptions =
      usersFilter?.options?.data?.filter((option: any) => option.checked) || [];

    if (defaultScopeOptions.length > 0) {
      this.dynamicGrid.filters['auditReportName'] = defaultScopeOptions;
    }

    if (defaultUserOptions.length > 0) {
      this.dynamicGrid.filters['userEmail'] = defaultUserOptions;
    }

    if (shouldRefresh && defaultUserOptions.length > 0) {
      this.dynamicGrid.fetchData();
    }
  }

}
